import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getCredentials, saveCredentials, testConnection, getSyncStatus, triggerSync,
  getChecklistAssignees, saveChecklistAssignees,
  getLeadQuestionMap, saveLeadQuestionMap,
  getAssigneeActiveLeads, bulkAssignLeads,
} from '../api';
import axios from 'axios';
import { useLanguage } from '../LanguageContext';
import { useAuth } from '../AuthContext';
import { useBrand } from '../BrandContext';
import { PAGE_KEYS, ACTION_KEYS, roleDefault, can } from '../permissions';
import { ALL_CLOSURE_STEPS, DEAL_OWNER } from '../components/UnitsDeals/constants';

// ── Deal-closure checklist → employee assignment (company-wide, all projects) ──
// ── Lead-form question → CRM field mapping editor ──
function QuestionMapEditor({ lang }) {
  const ar = lang === 'ar';
  const T = (a, e) => (ar ? a : e);
  const [map, setMap] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => { getLeadQuestionMap().then((r) => setMap(r.map || { budget: [] })); }, []);
  if (!map) return <div style={{ padding: 20, color: 'var(--text-secondary)' }}>…</div>;
  const FIELD_LABELS = { budget: T('الميزانية', 'Budget') };
  const fields = Object.keys(map);
  const setKw = (f, val) => setMap((m) => ({ ...m, [f]: val.split(',').map((s) => s.trim()).filter(Boolean) }));
  const save = async () => {
    setBusy(true); const r = await saveLeadQuestionMap(map); setBusy(false);
    setMsg(r.error ? { e: true, t: T('تعذّر الحفظ', 'Save failed') } : { e: false, t: T('تم الحفظ', 'Saved') });
  };
  const inp = { width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' };
  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ fontWeight: 800, marginBottom: 6 }}>{T('ربط أسئلة نماذج العملاء بالحقول', 'Lead-form question mapping')}</div>
      <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.8 }}>
        {T('عند وصول عميل من نموذج تيك توك/سناب، يُطابق عنوان كل سؤال بالكلمات أدناه ليُملأ الحقل تلقائيًا. وكل سؤال آخر يُحفظ في ملاحظات العميل كما هو — فلا تضيع أي إجابة.',
           'When a lead arrives from a TikTok/Snap form, each question title is matched against the keywords below to auto-fill the field. Every other question is saved to the lead\'s notes as-is — no answer is lost.')}
      </div>
      {fields.map((f) => (
        <div key={f} style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', marginBottom: 4 }}>{FIELD_LABELS[f] || f}</label>
          <input value={(map[f] || []).join(', ')} onChange={(e) => setKw(f, e.target.value)}
            placeholder={T('كلمات مفصولة بفواصل، مثل: budget, ميزانية, الميزانية', 'comma-separated keywords, e.g. budget, ميزانية')} style={inp} />
          <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 3 }}>
            {T('يطابق أي سؤال يحتوي عنوانه على إحدى هذه الكلمات (غير حساس لحالة الأحرف).', 'Matches any question whose title contains one of these words (case-insensitive).')}
          </div>
        </div>
      ))}
      {msg && <div style={{ margin: '8px 0', fontWeight: 700, color: msg.e ? '#ef4444' : '#16a34a' }}>{msg.t}</div>}
      <button onClick={save} disabled={busy} style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 800, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}>
        {busy ? '…' : T('حفظ', 'Save')}
      </button>
    </div>
  );
}

function ChecklistAssignments({ lang }) {
  const ar = lang === 'ar';
  const t = (a, e) => (ar ? a : e);
  const [assignees, setAssignees] = useState({});
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    (async () => {
      const [a, u] = await Promise.all([
        getChecklistAssignees(),
        axios.get('/api/auth/users').then(r => r.data.users || []).catch(() => []),
      ]);
      setAssignees(a.assignees || {});
      setEmployees(u.filter(x => x.active !== 0 && x.role !== 'broker').map(x => x.name).filter(Boolean));
      setLoading(false);
    })();
  }, []);

  const set = (key, val) => setAssignees(prev => ({ ...prev, [key]: val }));
  const save = async () => {
    setSaving(true);
    const res = await saveChecklistAssignees(assignees);
    setSaving(false);
    setMsg(res.error ? { type: 'error', text: res.error } : { type: 'ok', text: t('تم الحفظ', 'Saved') });
    setTimeout(() => setMsg(null), 3500);
  };

  const sel = { padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.82rem', minWidth: 200 };

  if (loading) return <div style={{ padding: 30, color: 'var(--text-secondary)' }}>...</div>;
  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 14, lineHeight: 1.6 }}>
        {t('عيّن كل خطوة من خطوات إغلاق الصفقة لموظف مسؤول (على مستوى الشركة، لكل المشاريع). يمكنك أيضًا تعيينها لموظف المبيعات الذي أنشأ الصفقة.',
          'Assign each deal-closure step to a responsible employee (company-wide, all projects). You can also assign it to the sales member who created the deal.')}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {ALL_CLOSURE_STEPS.map((s, i) => (
          <div key={s.en} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-secondary)' }}>
            <span style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--accent)', color: '#fff', fontSize: '0.72rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
            <span style={{ flex: 1, minWidth: 160, fontSize: '0.86rem', fontWeight: 600 }}>{ar ? s.ar : s.en}</span>
            <select value={assignees[s.en] || ''} onChange={e => set(s.en, e.target.value)} style={sel}>
              <option value="">{t('غير معيّن', 'Unassigned')}</option>
              <option value={DEAL_OWNER}>{t('موظف المبيعات صاحب الصفقة', 'The deal’s salesperson')}</option>
              {employees.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
        <button onClick={save} disabled={saving} style={{ padding: '10px 22px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: '0.88rem', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
          {saving ? '...' : t('حفظ', 'Save')}
        </button>
        {msg && <span style={{ fontSize: '0.82rem', color: msg.type === 'error' ? 'var(--danger, #dc2626)' : '#16a34a' }}>{msg.text}</span>}
      </div>
    </div>
  );
}

function PasswordInput({ label, value, onChange, placeholder, showLabel, hideLabel }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="input-group">
      <label>{label}</label>
      <div className="password-wrapper">
        <input type={visible ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
        <button type="button" className="password-toggle" onClick={() => setVisible(!visible)}>
          {visible ? (hideLabel || 'Hide') : (showLabel || 'Show')}
        </button>
      </div>
    </div>
  );
}

function TextInput({ label, value, onChange, placeholder }) {
  return (
    <div className="input-group">
      <label>{label}</label>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function SyncStatusBadge({ status, t }) {
  if (!status) return <span className="badge" style={{ background: 'rgba(139,143,163,0.15)', color: 'var(--text-secondary)' }}>{t('neverSynced')}</span>;
  if (status.status === 'success') {
    const time = status.completed_at ? new Date(status.completed_at).toLocaleString() : '';
    return <span className="badge badge-active" title={time}>Synced {status.records_synced || 0} records - {time}</span>;
  }
  if (status.status === 'failed') return <span className="badge badge-error" title={status.error_message}>{t('syncFailed')}</span>;
  if (status.status === 'started') return <span className="badge" style={{ background: 'rgba(99,102,241,0.15)', color: 'var(--accent)' }}>{t('syncing')}</span>;
  return null;
}

// ── User Management — shared between Employees and Brokers tabs ──
// `kind` is 'employee' (admin/manager/sales/viewer) or 'broker'.
// Broker users must be linked to a row in the brokers (companies) table.
function UserManagement({ t, kind, lang }) {
  const brand = useBrand();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isBrokerTab = kind === 'broker';
  // A non-admin only ever reaches the BROKER tab, and only with a brokers.*
  // permission. Each action maps to its own key: add → brokers.create,
  // activate/reset/rename → brokers.edit, delete → brokers.delete. The server
  // enforces the same mapping and only ever lets a non-admin touch broker accounts.
  const canCreate = isAdmin || (isBrokerTab && can(user, 'brokers.create'));
  const canEditUser = isAdmin || (isBrokerTab && can(user, 'brokers.edit'));
  const canDeleteUser = isAdmin || (isBrokerTab && can(user, 'brokers.delete'));
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const defaultRole = isBrokerTab ? 'broker' : 'sales';
  const [form, setForm] = useState({ name: '', email: '', password: '', role: defaultRole, broker_id: '', custom_role_id: '' });
  const [msg, setMsg] = useState(null);
  const [brokerCompanies, setBrokerCompanies] = useState([]);
  const [customRoles, setCustomRoles] = useState([]); // named roles from the الصلاحيات tab
  const [pwReset, setPwReset] = useState(null); // { name, email, password } after an admin reset
  const [deactivateModal, setDeactivateModal] = useState(null); // { user, ids } when a member has open leads

  const loadUsers = async () => {
    setLoading(true);
    try {
      // The broker tab lists broker accounts only; ?scope=broker lets a non-admin
      // brokers.* holder read them (admins still see everyone without it).
      const res = await axios.get('/api/auth/users', { params: isBrokerTab ? { scope: 'broker' } : {} });
      setUsers(res.data.users || []);
      setMsg(null);
    } catch (err) {
      // Surface the real reason instead of a misleading "no employees" empty state.
      const status = err.response?.status;
      setMsg({ type: 'error', text: status === 403
        ? (lang === 'ar' ? 'إدارة الموظفين متاحة لحساب المدير فقط — سجّل الدخول بحساب مدير' : 'Employee management is admin-only — log in with an admin account')
        : (lang === 'ar' ? 'تعذّر تحميل الموظفين، حاول مجددًا' : 'Failed to load employees, please retry') });
    }
    setLoading(false);
  };

  const loadBrokerCompanies = async () => {
    // Source from /api/brokers (page.brokers) so a non-admin broker manager can
    // load the company list too — /api/units-deals/brokers needs page.units_deals.
    try {
      const res = await axios.get('/api/brokers');
      setBrokerCompanies((res.data.brokers || []).filter(b => b.active !== 0));
    } catch {}
  };

  useEffect(() => {
    loadUsers();
    if (isBrokerTab) loadBrokerCompanies();
    else axios.get('/api/auth/roles').then(r => setCustomRoles(r.data.roles || [])).catch(() => {});
  }, [isBrokerTab]);

  const filtered = useMemo(() => {
    if (isBrokerTab) return users.filter(u => u.role === 'broker');
    return users.filter(u => u.role !== 'broker');
  }, [users, isBrokerTab]);

  const handleAdd = async () => {
    setMsg(null);
    // Inline validation with visible feedback (no more silent returns).
    if (!form.name.trim()) {
      setMsg({ type: 'error', text: lang === 'ar' ? 'الاسم مطلوب' : 'Name is required' });
      return;
    }
    if (!form.email.trim()) {
      setMsg({ type: 'error', text: lang === 'ar' ? 'البريد مطلوب' : 'Email is required' });
      return;
    }
    if (!form.password) {
      setMsg({ type: 'error', text: lang === 'ar' ? 'كلمة المرور مطلوبة' : 'Password is required' });
      return;
    }
    if (form.password.length < 8 || !/[A-Za-z]/.test(form.password) || !/[0-9]/.test(form.password)) {
      setMsg({ type: 'error', text: lang === 'ar' ? 'كلمة المرور يجب أن تكون 8 أحرف على الأقل وتحتوي على حروف وأرقام' : 'Password must be at least 8 chars and contain letters and numbers' });
      return;
    }
    if (isBrokerTab && !form.broker_id) {
      setMsg({ type: 'error', text: lang === 'ar' ? 'يجب اختيار شركة الوسيط' : 'Please select a broker company' });
      return;
    }
    try {
      const payload = { ...form, role: isBrokerTab ? 'broker' : form.role };
      if (!isBrokerTab) delete payload.broker_id;
      await axios.post('/api/auth/users', payload);
      setForm({ name: '', email: '', password: '', role: defaultRole, broker_id: '', custom_role_id: '' });
      setShowAdd(false);
      setMsg({ type: 'success', text: isBrokerTab ? t('settingsBrokerCreated') : t('settingsUserCreated') });
      loadUsers();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || (lang === 'ar' ? 'فشل الإنشاء' : 'Failed to create') });
    }
  };

  // Inline broker company creation so admins do not have to leave the page.
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newCompanyCommType, setNewCompanyCommType] = useState('percentage'); // 'percentage' | 'fixed'
  const [newCompanyValue, setNewCompanyValue] = useState('2');
  const [creatingCompany, setCreatingCompany] = useState(false);
  const createBrokerCompany = async () => {
    const name = newCompanyName.trim();
    if (!name) return;
    setCreatingCompany(true);
    try {
      const payload = { name, type: 'company', comm_type: newCompanyCommType };
      if (newCompanyCommType === 'fixed') payload.default_amt = Number(newCompanyValue) || 0;
      else payload.default_pct = Number(newCompanyValue) || 2;
      await axios.post('/api/units-deals/brokers', payload);
      setNewCompanyName('');
      setNewCompanyValue(newCompanyCommType === 'fixed' ? '' : '2');
      await loadBrokerCompanies();
      // Auto-select the newly created company.
      try {
        const res = await axios.get('/api/units-deals/brokers');
        const found = (res.data.brokers || []).find(b => b.name === name);
        if (found) setForm(p => ({ ...p, broker_id: String(found.id) }));
      } catch {}
      setMsg({ type: 'success', text: lang === 'ar' ? 'تم إضافة الشركة' : 'Company added' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || (lang === 'ar' ? 'فشل إضافة الشركة' : 'Failed to add company') });
    }
    setCreatingCompany(false);
  };

  const roleColors = { admin: 'var(--accent)', manager: 'var(--warning)', sales: '#3b82f6', broker: '#f59e0b', viewer: 'var(--text-secondary)' };

  // Toggle a user between active and inactive. For sales users, the server
  // mirrors the flag into crm_sales_team so the closer dropdown updates
  // immediately. Admins cannot deactivate the last active admin.
  const toggleActive = async (u) => {
    const goingInactive = !!u.active;
    // Before deactivating an internal member, check whether they still hold open
    // (non-lost) leads — if so, let the admin decide (keep / reassign to one /
    // distribute by %) before they vanish as a valid assignee. Broker accounts are
    // scoped to their own company, so they're left on the plain confirm path.
    if (goingInactive && !isBrokerTab) {
      const res = await getAssigneeActiveLeads(u.name);
      if ((res.ids || []).length > 0) { setDeactivateModal({ user: u, ids: res.ids }); return; }
    }
    const verb = goingInactive
      ? (lang === 'ar' ? 'تعطيل' : 'Deactivate')
      : (lang === 'ar' ? 'تفعيل' : 'Activate');
    const confirmMsg = goingInactive
      ? (lang === 'ar'
          ? `${verb} ${u.name}؟ لن يظهر كمنفّذ صفقات ولن يتمكن من تسجيل الدخول.`
          : `${verb} ${u.name}? They will not appear as a closer and cannot log in.`)
      : (lang === 'ar'
          ? `${verb} ${u.name}؟`
          : `${verb} ${u.name}?`);
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.patch(`/api/auth/users/${u.id}/active`, { active: goingInactive ? 0 : 1 });
      setMsg({
        type: 'success',
        text: lang === 'ar'
          ? `${verb} ${u.name} بنجاح`
          : `${u.name} ${goingInactive ? 'deactivated' : 'activated'}`,
      });
      loadUsers();
    } catch (err) {
      setMsg({
        type: 'error',
        text: err.response?.data?.error || (lang === 'ar' ? 'فشل التحديث' : 'Update failed'),
      });
    }
  };

  // Deactivate a member who has open leads, after the admin picks what happens to them.
  // disposition: { mode: 'keep' | 'reassign' | 'distribute', assignedTo?, distribution? }.
  // Reassign happens BEFORE deactivation so a failure never strands leads on a disabled user.
  const confirmDeactivateWithLeads = async (disposition) => {
    if (!deactivateModal) return;
    const { user: u, ids } = deactivateModal;
    try {
      if (disposition.mode === 'reassign' && disposition.assignedTo) {
        const r = await bulkAssignLeads(ids, disposition.assignedTo);
        if (r.error) throw new Error(r.error);
      } else if (disposition.mode === 'distribute') {
        // Split the ids into contiguous buckets sized by each member's percentage;
        // the last member absorbs any rounding remainder.
        let offset = 0;
        const picks = disposition.distribution.filter(d => Number(d.pct) > 0);
        for (let i = 0; i < picks.length; i++) {
          const take = i === picks.length - 1
            ? ids.length - offset
            : Math.min(Math.round(ids.length * Number(picks[i].pct) / 100), ids.length - offset);
          const chunk = ids.slice(offset, offset + take);
          offset += take;
          if (chunk.length) { const r = await bulkAssignLeads(chunk, picks[i].name); if (r.error) throw new Error(r.error); }
        }
      }
      // mode 'keep' → leads stay with the (now inactive) member.
      await axios.patch(`/api/auth/users/${u.id}/active`, { active: 0 });
      setMsg({ type: 'success', text: lang === 'ar' ? `تم تعطيل ${u.name}` : `${u.name} deactivated` });
      setDeactivateModal(null);
      loadUsers();
    } catch (err) {
      setMsg({ type: 'error', text: err.message || (lang === 'ar' ? 'فشل التحديث' : 'Update failed') });
    }
  };

  // Let a non-sales employee (e.g. a manager who also closes) appear in the
  // deal-registration closer dropdown. Sales users are closers by role, so this
  // is only offered for non-sales rows. It does NOT affect lead routing.
  const toggleDealCloser = async (u) => {
    const next = u.deal_closer ? 0 : 1;
    try {
      await axios.patch(`/api/auth/users/${u.id}/deal-closer`, { deal_closer: next });
      setMsg({
        type: 'success',
        text: lang === 'ar'
          ? (next ? `${u.name} أصبح متاحاً كمنفّذ صفقات` : `${u.name} لم يعد متاحاً كمنفّذ صفقات`)
          : `${u.name} ${next ? 'is now selectable as a deal closer' : 'removed from deal closers'}`,
      });
      loadUsers();
    } catch (err) {
      setMsg({
        type: 'error',
        text: err.response?.data?.error || (lang === 'ar' ? 'فشل التحديث' : 'Update failed'),
      });
    }
  };

  // Admin-only password reset for any employee or broker. The server generates a
  // new password and returns it once; we surface it for the admin to hand over.
  const resetPassword = async (u) => {
    const confirmMsg = lang === 'ar'
      ? `إعادة تعيين كلمة مرور ${u.name}؟ ستتوقف كلمة المرور الحالية عن العمل وتُنشأ كلمة جديدة.`
      : `Reset ${u.name}'s password? Their current password will stop working and a new one will be generated.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await axios.patch(`/api/auth/users/${u.id}/reset-password`);
      setPwReset({ name: u.name, email: u.email, password: res.data.password });
    } catch (err) {
      setMsg({
        type: 'error',
        text: err.response?.data?.error || (lang === 'ar' ? 'فشل إعادة تعيين كلمة المرور' : 'Password reset failed'),
      });
    }
  };

  // Rename a user (display name). The server cascades the change to their lead
  // attribution (scoped to their broker company for broker users).
  const editUser = async (u) => {
    const name = window.prompt(lang === 'ar' ? `الاسم الجديد لـ ${u.name}` : `New name for ${u.name}`, u.name);
    if (name === null) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === u.name) return;
    try {
      await axios.patch(`/api/auth/users/${u.id}`, { name: trimmed });
      setMsg({ type: 'success', text: lang === 'ar' ? 'تم تحديث الاسم' : 'Name updated' });
      loadUsers();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || (lang === 'ar' ? 'فشل التحديث' : 'Update failed') });
    }
  };

  const deleteUser = async (u) => {
    const confirmMsg = lang === 'ar'
      ? `حذف ${u.name} نهائيًا؟ لا يمكن التراجع.`
      : `Permanently delete ${u.name}? This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.delete(`/api/auth/users/${u.id}`);
      setMsg({ type: 'success', text: lang === 'ar' ? `تم حذف ${u.name}` : `${u.name} deleted` });
      loadUsers();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || (lang === 'ar' ? 'فشل الحذف' : 'Delete failed') });
    }
  };

  return (
    <div className="card mb-6">
      <div className="card-header">
        <h3>{isBrokerTab ? t('settingsBrokerManagement') : t('settingsEmployeeManagement')}</h3>
        {canCreate && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(!showAdd)}>
            {showAdd ? t('crmCancel') : '+ ' + (isBrokerTab ? t('settingsAddBroker') : t('settingsAddUser'))}
          </button>
        )}
      </div>

      {msg && (
        <div style={{
          background: msg.type === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
          border: `1px solid ${msg.type === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`,
          borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: '0.85rem',
          color: msg.type === 'error' ? 'var(--error)' : 'var(--success)',
        }}>{msg.text}</div>
      )}

      {pwReset && (
        <div style={{
          background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.35)',
          borderRadius: 10, padding: '12px 14px', marginBottom: 16,
        }}>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
            {lang === 'ar'
              ? `كلمة المرور الجديدة لـ ${pwReset.name} (${pwReset.email}) — انسخها الآن، لن تظهر مرة أخرى:`
              : `New password for ${pwReset.name} (${pwReset.email}) — copy it now, it won't be shown again:`}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <code style={{ direction: 'ltr', fontSize: '1rem', fontWeight: 800, padding: '6px 10px', background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border)', letterSpacing: '0.04em' }}>{pwReset.password}</code>
            <button className="btn btn-secondary btn-sm" onClick={() => { navigator.clipboard?.writeText(pwReset.password); }}>
              {lang === 'ar' ? 'نسخ' : 'Copy'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setPwReset(null)}>
              {lang === 'ar' ? 'إغلاق' : 'Close'}
            </button>
          </div>
        </div>
      )}

      {deactivateModal && (
        <DeactivateLeadsModal
          user={deactivateModal.user}
          count={deactivateModal.ids.length}
          members={users.filter(x => x.active !== 0 && x.role !== 'broker' && x.name !== deactivateModal.user.name).map(x => x.name).filter(Boolean)}
          lang={lang}
          onCancel={() => setDeactivateModal(null)}
          onConfirm={confirmDeactivateWithLeads}
        />
      )}

      {showAdd && (
        <div style={{ background: 'var(--bg-primary)', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid var(--border)' }}>
          <div className="crm-form-grid">
            <div className="input-group"><label>{t('settingsName')}</label><input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ahmed" /></div>
            <div className="input-group"><label>{t('loginEmail')}</label><input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder={brand.adminEmail} style={{ direction: 'ltr' }} /></div>
            <div className="input-group">
              <label>{t('loginPassword')}</label>
              <input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="••••••••" style={{ direction: 'ltr' }} />
              <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                {lang === 'ar' ? '8 أحرف على الأقل، حروف وأرقام' : 'Min 8 chars, letters + numbers'}
              </div>
            </div>
            {isBrokerTab ? (
              <div className="input-group" style={{ gridColumn: '1 / -1' }}>
                <label style={{ color: 'var(--accent)', fontWeight: 700 }}>
                  {lang === 'ar' ? 'شركة الوسيط' : 'Broker Company'} <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select
                  value={form.broker_id}
                  onChange={e => setForm(p => ({ ...p, broker_id: e.target.value }))}
                  required
                  style={{
                    border: form.broker_id ? '1px solid var(--border)' : '1px solid rgba(245,158,11,0.5)',
                    background: form.broker_id ? 'var(--bg-card)' : 'rgba(245,158,11,0.05)',
                  }}
                >
                  <option value="">{lang === 'ar' ? '— اختر الشركة —' : '— Select a company —'}</option>
                  {brokerCompanies.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
                {/* Inline create-new-company UI (admin only — needs Units & Deals access) */}
                {isAdmin && (
                <>
                <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    value={newCompanyName}
                    onChange={e => setNewCompanyName(e.target.value)}
                    placeholder={lang === 'ar' ? '+ إضافة شركة جديدة (مثال: شركة ريفا)' : '+ Add new company (e.g. شركة ريفا)'}
                    style={{
                      flex: '1 1 200px',
                      padding: '8px 12px',
                      border: '1px dashed var(--border)',
                      borderRadius: 8,
                      background: 'transparent',
                      fontSize: '0.85rem',
                    }}
                  />
                  <select
                    value={newCompanyCommType}
                    onChange={e => { setNewCompanyCommType(e.target.value); setNewCompanyValue(e.target.value === 'fixed' ? '' : '2'); }}
                    style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-primary)', fontSize: '0.82rem', cursor: 'pointer' }}
                  >
                    <option value="percentage">{lang === 'ar' ? 'نسبة %' : 'Percentage %'}</option>
                    <option value="fixed">{lang === 'ar' ? 'مبلغ ثابت (ريال)' : 'Fixed (SAR)'}</option>
                  </select>
                  <input
                    type="number"
                    min="0"
                    value={newCompanyValue}
                    onChange={e => setNewCompanyValue(e.target.value)}
                    placeholder={newCompanyCommType === 'fixed' ? (lang === 'ar' ? 'المبلغ (ريال)' : 'Amount SAR') : '%'}
                    style={{ width: 120, padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-primary)', fontSize: '0.82rem' }}
                  />
                  <button
                    type="button"
                    onClick={createBrokerCompany}
                    disabled={!newCompanyName.trim() || creatingCompany}
                    style={{
                      padding: '8px 14px',
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      background: 'rgba(99,102,241,0.1)',
                      border: '1px solid rgba(99,102,241,0.35)',
                      color: 'var(--accent)',
                      borderRadius: 8,
                      cursor: newCompanyName.trim() ? 'pointer' : 'not-allowed',
                      opacity: newCompanyName.trim() ? 1 : 0.5,
                    }}
                  >
                    {creatingCompany ? '...' : (lang === 'ar' ? 'إضافة' : 'Add')}
                  </button>
                </div>
                {brokerCompanies.length === 0 && !creatingCompany && (
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 6 }}>
                    {lang === 'ar'
                      ? 'لا توجد شركات. أضف شركة من الحقل أعلاه أو من صفحة الوحدات والصفقات.'
                      : 'No companies yet. Add one above or from the Units & Deals page.'}
                  </div>
                )}
                </>
                )}
              </div>
            ) : (
              <div className="input-group">
                <label>{t('settingsRole')}</label>
                <select
                  value={form.custom_role_id ? `custom:${form.custom_role_id}` : form.role}
                  onChange={e => {
                    const v = e.target.value;
                    if (v.startsWith('custom:')) setForm(p => ({ ...p, role: 'viewer', custom_role_id: v.slice(7) }));
                    else setForm(p => ({ ...p, role: v, custom_role_id: '' }));
                  }}>
                  <optgroup label={lang === 'ar' ? 'الأدوار الأساسية' : 'Base roles'}>
                    <option value="admin">{t('settingsRoleAdmin')}</option>
                    <option value="manager">{t('settingsRoleManager')}</option>
                    <option value="sales">{t('settingsRoleSales') || 'Sales'}</option>
                    <option value="viewer">{t('settingsRoleViewer')}</option>
                  </optgroup>
                  {customRoles.length > 0 && (
                    <optgroup label={lang === 'ar' ? 'أدوار مخصّصة (من الصلاحيات)' : 'Custom roles (from Permissions)'}>
                      {customRoles.map(r => (
                        <option key={r.id} value={`custom:${r.id}`}>{lang === 'ar' && r.name_ar ? r.name_ar : r.name}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
                {form.custom_role_id && (
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                    {lang === 'ar'
                      ? 'دور مخصّص: يُنشأ الموظف بصلاحيات هذا الدور فقط — دون أي ارتباط بالمبيعات أو توزيع العملاء (أساس محايد).'
                      : 'Custom role: employee gets only this role’s permissions — no sales access or lead distribution (neutral base).'}
                  </div>
                )}
              </div>
            )}
          </div>
          <button className="btn btn-primary" onClick={handleAdd}>
            {isBrokerTab ? t('settingsCreateBroker') : t('settingsCreateUser')}
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>...</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          {isBrokerTab ? t('settingsNoBrokers') : t('settingsNoEmployees')}
        </div>
      ) : (
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('settingsName')}</th>
                <th>{t('loginEmail')}</th>
                {isBrokerTab ? (
                  <th>{lang === 'ar' ? 'شركة الوسيط' : 'Broker Company'}</th>
                ) : (
                  <th>{t('settingsRole')}</th>
                )}
                {!isBrokerTab && (
                  <th style={{ textAlign: 'center' }}>{lang === 'ar' ? 'منفّذ صفقات' : 'Deal closer'}</th>
                )}
                <th>{t('settingsLastLogin')}</th>
                <th>{t('status')}</th>
                <th style={{ textAlign: 'center' }}>{lang === 'ar' ? 'إجراء' : 'Action'}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id}>
                  <td style={{ fontWeight: 600 }}>{u.name}</td>
                  <td style={{ direction: 'ltr', fontSize: '0.85rem' }}>{u.email}</td>
                  {isBrokerTab ? (
                    <td>
                      {u.broker_company ? (
                        <span className="badge" style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}>
                          {u.broker_company}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>—</span>
                      )}
                    </td>
                  ) : (
                    <td>
                      <span className="badge" style={{ background: (roleColors[u.role] || 'var(--text-secondary)') + '22', color: roleColors[u.role] || 'var(--text-secondary)' }}>
                        {t('settingsRole' + u.role.charAt(0).toUpperCase() + u.role.slice(1))}
                      </span>
                    </td>
                  )}
                  {!isBrokerTab && (
                    <td style={{ textAlign: 'center' }}>
                      {u.role === 'sales' ? (
                        <span style={{ fontSize: '0.74rem', color: 'var(--text-secondary)' }}>
                          {lang === 'ar' ? 'تلقائي (مبيعات)' : 'Auto (sales)'}
                        </span>
                      ) : (
                        <button
                          onClick={() => toggleDealCloser(u)}
                          title={lang === 'ar' ? 'إتاحته كمنفّذ صفقات عند تسجيل صفقة' : 'Make selectable as a closer when registering a deal'}
                          style={{
                            padding: '5px 12px',
                            fontSize: '0.74rem',
                            fontWeight: 800,
                            borderRadius: 6,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            background: u.deal_closer ? 'rgba(34,197,94,0.12)' : 'transparent',
                            border: `1.5px solid ${u.deal_closer ? 'rgba(34,197,94,0.5)' : 'var(--border)'}`,
                            color: u.deal_closer ? '#22c55e' : 'var(--text-secondary)',
                          }}
                        >
                          {u.deal_closer
                            ? (lang === 'ar' ? '✓ متاح' : '✓ Enabled')
                            : (lang === 'ar' ? 'غير متاح' : 'Off')}
                        </button>
                      )}
                    </td>
                  )}
                  <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {u.last_login ? new Date(u.last_login).toLocaleString() : '—'}
                  </td>
                  <td><span className={`badge badge-${u.active ? 'active' : 'paused'}`}>{u.active ? t('active') : t('paused')}</span></td>
                  <td style={{ textAlign: 'center' }}>
                    {(canEditUser || canDeleteUser) ? (
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {canEditUser && <button
                        onClick={() => resetPassword(u)}
                        title={lang === 'ar' ? 'إنشاء كلمة مرور جديدة لهذا المستخدم' : 'Generate a new password for this user'}
                        style={{
                          padding: '5px 12px',
                          fontSize: '0.74rem',
                          fontWeight: 800,
                          borderRadius: 6,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          background: 'rgba(99,102,241,0.10)',
                          border: '1.5px solid rgba(99,102,241,0.45)',
                          color: '#6366f1',
                        }}
                      >
                        {lang === 'ar' ? 'كلمة المرور' : 'Reset password'}
                      </button>}
                      {canEditUser && <button
                        onClick={() => toggleActive(u)}
                        style={{
                          padding: '5px 12px',
                          fontSize: '0.74rem',
                          fontWeight: 800,
                          borderRadius: 6,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          background: u.active ? 'rgba(239,68,68,0.10)' : 'rgba(34,197,94,0.12)',
                          border: `1.5px solid ${u.active ? 'rgba(239,68,68,0.45)' : 'rgba(34,197,94,0.5)'}`,
                          color: u.active ? '#ef4444' : '#22c55e',
                        }}
                      >
                        {u.active
                          ? (lang === 'ar' ? 'تعطيل' : 'Deactivate')
                          : (lang === 'ar' ? 'تفعيل' : 'Activate')}
                      </button>}
                      {canEditUser && <button
                        onClick={() => editUser(u)}
                        title={lang === 'ar' ? 'تعديل اسم المستخدم' : 'Edit display name'}
                        style={{
                          padding: '5px 12px', fontSize: '0.74rem', fontWeight: 800, borderRadius: 6,
                          cursor: 'pointer', whiteSpace: 'nowrap', background: 'transparent',
                          border: '1.5px solid var(--border)', color: 'var(--text-secondary)',
                        }}
                      >
                        {lang === 'ar' ? 'تعديل الاسم' : 'Edit name'}
                      </button>}
                      {canDeleteUser && <button
                        onClick={() => deleteUser(u)}
                        title={lang === 'ar' ? 'حذف المستخدم نهائيًا' : 'Delete this user'}
                        style={{
                          padding: '5px 12px', fontSize: '0.74rem', fontWeight: 800, borderRadius: 6,
                          cursor: 'pointer', whiteSpace: 'nowrap', background: 'rgba(239,68,68,0.10)',
                          border: '1.5px solid rgba(239,68,68,0.45)', color: '#ef4444',
                        }}
                      >
                        {lang === 'ar' ? 'حذف' : 'Delete'}
                      </button>}
                    </div>
                    ) : (
                      <span style={{ fontSize: '0.74rem', color: 'var(--text-secondary)' }}>
                        {lang === 'ar' ? 'يُدار من صفحة الوسطاء' : 'Managed on Brokers page'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Lead Distribution — admin config for how new leads auto-assign ──
// mode 'equal'      → round-robin across active members (fewest-today)
// mode 'percentage' → weighted by each active member's share %
// Modal shown when deactivating a member who still has open leads: keep them, reassign
// all to one member, or distribute by percentage across several. The parent owns the
// actual lead ids and does the moving — this only collects the admin's choice.
function DeactivateLeadsModal({ user, count, members, lang, onCancel, onConfirm }) {
  const L = (a, e) => (lang === 'ar' ? a : e);
  const [mode, setMode] = useState('reassign');
  const [assignedTo, setAssignedTo] = useState('');
  const [pcts, setPcts] = useState({}); // name -> percentage string

  const distTotal = members.reduce((s, m) => s + (Number(pcts[m]) || 0), 0);
  const distValid = distTotal === 100 && Object.values(pcts).some(v => Number(v) > 0);
  const canConfirm = mode === 'keep'
    || (mode === 'reassign' && assignedTo)
    || (mode === 'distribute' && distValid);

  const submit = () => {
    if (!canConfirm) return;
    if (mode === 'distribute') onConfirm({ mode, distribution: members.map(m => ({ name: m, pct: Number(pcts[m]) || 0 })) });
    else if (mode === 'reassign') onConfirm({ mode, assignedTo });
    else onConfirm({ mode: 'keep' });
  };

  const radioRow = (val, label) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', cursor: 'pointer', fontWeight: 600, fontSize: '0.86rem' }}>
      <input type="radio" checked={mode === val} onChange={() => setMode(val)} /> {label}
    </label>
  );

  return (
    <div className="crm-modal-overlay" onClick={onCancel}>
      <div className="crm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <h3 style={{ margin: '0 0 6px', fontSize: '1.05rem', fontWeight: 800 }}>{L('تعطيل', 'Deactivate')} {user.name}</h3>
        <p style={{ fontSize: '0.84rem', color: 'var(--text-secondary)', margin: '0 0 12px' }}>
          {L(`لدى هذا الموظف ${count} عميل مفتوح (غير مفقود). ماذا تريد أن تفعل بها؟`,
             `This member has ${count} open (non-lost) lead(s). What should happen to them?`)}
        </p>

        {radioRow('reassign', L('نقلها إلى موظف واحد', 'Reassign all to one member'))}
        {mode === 'reassign' && (
          <select value={assignedTo} onChange={e => setAssignedTo(e.target.value)} style={{ width: '100%', marginBottom: 6 }}>
            <option value="">— {L('اختر الموظف', 'Select member')} —</option>
            {members.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        )}

        {radioRow('distribute', L('توزيعها بنسب على عدة موظفين', 'Distribute by percentage'))}
        {mode === 'distribute' && (
          <div style={{ marginBottom: 6 }}>
            {members.length === 0 && <div style={{ fontSize: '0.8rem', color: 'var(--error)' }}>{L('لا يوجد موظفون آخرون', 'No other members available')}</div>}
            {members.map(m => (
              <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ flex: 1, fontSize: '0.82rem' }}>{m}</span>
                <input type="number" min="0" max="100" value={pcts[m] || ''} placeholder="0"
                  onChange={e => setPcts(p => ({ ...p, [m]: e.target.value }))} style={{ width: 70, direction: 'ltr' }} />
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>%</span>
              </div>
            ))}
            <div style={{ fontSize: '0.78rem', fontWeight: 700, marginTop: 4, color: distTotal === 100 ? 'var(--success)' : 'var(--error)' }}>
              {L('المجموع', 'Total')}: {distTotal}% {distTotal !== 100 && L('(يجب أن يساوي 100)', '(must equal 100)')}
            </div>
          </div>
        )}

        {radioRow('keep', L('الإبقاء عليها مع الموظف المُعطَّل', 'Keep them with the deactivated member'))}

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button className="btn btn-secondary" onClick={onCancel} style={{ flex: 1 }}>{L('إلغاء', 'Cancel')}</button>
          <button className="btn btn-primary" onClick={submit} disabled={!canConfirm} style={{ flex: 2, opacity: canConfirm ? 1 : 0.6 }}>{L('تعطيل', 'Deactivate')}</button>
        </div>
      </div>
    </div>
  );
}

function LeadDistribution({ lang }) {
  const [mode, setMode] = useState('equal');
  const [members, setMembers] = useState([]);
  const [dailyLimit, setDailyLimit] = useState(35);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  // JS getDay() order: 0=Sun..6=Sat. Saudi week starts Sunday.
  const weekdays = lang === 'ar'
    ? [['0', '\u0623\u062D\u062F'], ['1', '\u0627\u062B\u0646\u064A\u0646'], ['2', '\u062B\u0644\u0627\u062B\u0627\u0621'], ['3', '\u0623\u0631\u0628\u0639\u0627\u0621'], ['4', '\u062E\u0645\u064A\u0633'], ['5', '\u062C\u0645\u0639\u0629'], ['6', '\u0633\u0628\u062A']]
    : [['0', 'Sun'], ['1', 'Mon'], ['2', 'Tue'], ['3', 'Wed'], ['4', 'Thu'], ['5', 'Fri'], ['6', 'Sat']];

  const normMembers = (arr) => (arr || []).map(m => ({
    ...m,
    weight: Number(m.weight) || 0,
    dailyKpi: Number(m.dailyKpi) || 0,
    daysOff: Array.isArray(m.daysOff) ? m.daysOff.map(Number) : [],
  }));

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/leads/distribution');
      setMode(res.data.mode || 'equal');
      setMembers(normMembers(res.data.members));
      if (res.data.dailyFollowupLimit !== undefined) setDailyLimit(Number(res.data.dailyFollowupLimit) || 0);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleActive = (name) => setMembers(ms => ms.map(m => m.name === name ? { ...m, active: !m.active } : m));
  const setWeight = (name, v) => {
    const n = Math.max(0, Math.min(100, Math.round(Number(v) || 0)));
    setMembers(ms => ms.map(m => m.name === name ? { ...m, weight: n } : m));
  };
  const setKpi = (name, v) => {
    const n = Math.max(0, Math.round(Number(v) || 0));
    setMembers(ms => ms.map(m => m.name === name ? { ...m, dailyKpi: n } : m));
  };
  const toggleDayOff = (name, dow) => {
    const d = Number(dow);
    setMembers(ms => ms.map(m => {
      if (m.name !== name) return m;
      const has = m.daysOff.includes(d);
      return { ...m, daysOff: has ? m.daysOff.filter(x => x !== d) : [...m.daysOff, d] };
    }));
  };

  const activeMembers = members.filter(m => m.active);
  const weightSum = activeMembers.reduce((s, m) => s + (Number(m.weight) || 0), 0);
  const sumOk = mode !== 'percentage' || weightSum === 100;

  const save = async () => {
    setMsg(null);
    if (mode === 'percentage') {
      if (activeMembers.length === 0) {
        setMsg({ type: 'error', text: lang === 'ar' ? 'يجب تفعيل عضو واحد على الأقل' : 'Activate at least one member' });
        return;
      }
      if (weightSum !== 100) {
        setMsg({ type: 'error', text: lang === 'ar' ? `مجموع النسب يجب أن يساوي 100% (الحالي ${weightSum}%)` : `Percentages must total 100% (currently ${weightSum}%)` });
        return;
      }
    }
    setSaving(true);
    try {
      const res = await axios.put('/api/leads/distribution', { mode, members, dailyFollowupLimit: dailyLimit });
      setMode(res.data.mode || 'equal');
      setMembers(normMembers(res.data.members));
      if (res.data.dailyFollowupLimit !== undefined) setDailyLimit(Number(res.data.dailyFollowupLimit) || 0);
      setMsg({ type: 'success', text: lang === 'ar' ? 'تم حفظ إعدادات التوزيع' : 'Distribution settings saved' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || (lang === 'ar' ? 'فشل الحفظ' : 'Failed to save') });
    }
    setSaving(false);
  };

  return (
    <div className="card mb-6">
      <div className="card-header">
        <h3>{lang === 'ar' ? 'توزيع العملاء المحتملين' : 'Lead Distribution'}</h3>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !sumOk}>
          {saving ? '...' : (lang === 'ar' ? 'حفظ' : 'Save')}
        </button>
      </div>

      <p className="text-secondary" style={{ marginBottom: 16, fontSize: '0.85rem' }}>
        {lang === 'ar'
          ? '\u0643\u064A\u0641 \u064A\u062A\u0645 \u062A\u0648\u0632\u064A\u0639 \u0627\u0644\u0639\u0645\u0644\u0627\u0621 \u0627\u0644\u062C\u062F\u062F \u062A\u0644\u0642\u0627\u0626\u064A\u0627\u064B\u060C \u0648\u0627\u0644\u0647\u062F\u0641 \u0627\u0644\u064A\u0648\u0645\u064A \u0648\u0623\u064A\u0627\u0645 \u0625\u062C\u0627\u0632\u0629 \u0643\u0644 \u0639\u0636\u0648. \u0641\u064A \u0623\u064A\u0627\u0645 \u0627\u0644\u0625\u062C\u0627\u0632\u0629 \u0644\u0627 \u062A\u064F\u0633\u0646\u062F \u0639\u0645\u0644\u0627\u0621 \u0644\u0644\u0639\u0636\u0648 (\u0625\u0644\u0627 \u0625\u0630\u0627 \u0643\u0627\u0646 \u0627\u0644\u062C\u0645\u064A\u0639 \u0641\u064A \u0625\u062C\u0627\u0632\u0629).'
          : 'How new leads are auto-assigned, plus each rep\u2019s daily target and weekly days off. On a day off a rep gets no new leads (unless the whole team is off).'}
      </p>

      {msg && (
        <div style={{
          background: msg.type === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
          border: `1px solid ${msg.type === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`,
          borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: '0.85rem',
          color: msg.type === 'error' ? 'var(--error)' : 'var(--success)',
        }}>{msg.text}</div>
      )}

      {/* Mode toggle */}
      <div className="tabs" style={{ display: 'inline-flex', marginBottom: 16 }}>
        <button className={`tab ${mode === 'equal' ? 'active' : ''}`} onClick={() => setMode('equal')}>
          {lang === 'ar' ? 'بالتساوي' : 'Equal'}
        </button>
        <button className={`tab ${mode === 'percentage' ? 'active' : ''}`} onClick={() => setMode('percentage')}>
          {lang === 'ar' ? 'نسبة مئوية' : 'Percentage'}
        </button>
      </div>

      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 16 }}>
        {mode === 'equal'
          ? (lang === 'ar' ? 'يتم التوزيع بالتساوي على الأعضاء المفعّلين.' : 'Leads are split evenly across active members.')
          : (lang === 'ar' ? 'حدّد نسبة كل عضو مفعّل — يجب أن يكون المجموع 100%.' : 'Set each active member\'s share — must total 100%.')}
      </div>

      {/* Daily follow-up / visit cap per rep */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap', padding: '10px 12px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 10 }}>
        <label style={{ fontSize: '0.82rem', fontWeight: 700 }}>
          {lang === 'ar' ? 'الحد اليومي للمتابعات/الزيارات لكل موظف مبيعات' : 'Daily follow-ups/visits limit per rep'}
        </label>
        <input
          type="number" min="0"
          value={dailyLimit}
          onChange={(e) => setDailyLimit(Math.max(0, Math.round(Number(e.target.value) || 0)))}
          style={{ width: 90, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '0.9rem' }}
        />
        <span style={{ fontSize: '0.74rem', color: 'var(--text-secondary)' }}>
          {lang === 'ar' ? '0 = بدون حد. عند التجاوز يُطلب من موظف المبيعات تأكيد التجاوز.' : '0 = no limit. Reps confirm to exceed.'}
        </span>
      </div>

      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>...</div>
      ) : members.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          {lang === 'ar' ? 'لا يوجد أعضاء في فريق المبيعات.' : 'No sales team members.'}
        </div>
      ) : (
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>{lang === 'ar' ? 'العضو' : 'Member'}</th>
                <th style={{ textAlign: 'center' }}>{lang === 'ar' ? 'مفعّل' : 'Active'}</th>
                <th style={{ textAlign: 'center' }}>{lang === 'ar' ? 'هدف يومي' : 'Daily KPI'}</th>
                <th style={{ textAlign: 'center' }}>{lang === 'ar' ? 'أيام الإجازة' : 'Days Off'}</th>
                {mode === 'percentage' && <th style={{ textAlign: 'center' }}>{lang === 'ar' ? 'النسبة %' : 'Share %'}</th>}
              </tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr key={m.name}>
                  <td style={{ fontWeight: 600 }}>{m.name}</td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={!!m.active} onChange={() => toggleActive(m.name)} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="number" min="0" value={m.dailyKpi}
                      onChange={e => setKpi(m.name, e.target.value)}
                      style={{ width: 70, textAlign: 'center' }}
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {weekdays.map(([dow, label]) => {
                        const on = m.daysOff.includes(Number(dow));
                        return (
                          <button
                            key={dow}
                            type="button"
                            onClick={() => toggleDayOff(m.name, dow)}
                            title={label}
                            style={{
                              padding: '3px 7px',
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              borderRadius: 5,
                              cursor: 'pointer',
                              background: on ? 'rgba(239,68,68,0.12)' : 'transparent',
                              border: `1px solid ${on ? 'rgba(239,68,68,0.45)' : 'var(--border)'}`,
                              color: on ? '#ef4444' : 'var(--text-secondary)',
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  {mode === 'percentage' && (
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="number" min="0" max="100" value={m.weight}
                        disabled={!m.active}
                        onChange={e => setWeight(m.name, e.target.value)}
                        style={{ width: 80, textAlign: 'center', opacity: m.active ? 1 : 0.4 }}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            {mode === 'percentage' && (
              <tfoot>
                <tr>
                  <td style={{ fontWeight: 700, textAlign: 'right' }}>{lang === 'ar' ? 'المجموع' : 'Total'}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td style={{ textAlign: 'center', fontWeight: 800, color: weightSum === 100 ? 'var(--success)' : 'var(--error)' }}>
                    {weightSum}%
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

// ── User Permissions — admin-only per-user page/action overrides ──
// Empty (no override) = role defaults, i.e. exactly today's access. An admin
// can tick/untick individual pages and actions for any non-admin, non-broker
// user. Saving stores an explicit map; "Reset" clears it back to role defaults.
const PERM_PAGE_LABELS = {
  'page.executive': ['Executive', '\u0644\u0648\u062D\u0629 \u0627\u0644\u0645\u062F\u064A\u0631'],
  'page.channels': ['Channels', '\u0627\u0644\u0642\u0646\u0648\u0627\u062A'],
  'page.units_deals': ['Units & Deals', '\u0627\u0644\u0648\u062D\u062F\u0627\u062A \u0648\u0627\u0644\u0635\u0641\u0642\u0627\u062A'],
  'page.leads': ['Leads', '\u0627\u0644\u0639\u0645\u0644\u0627\u0621 \u0627\u0644\u0645\u062D\u062A\u0645\u0644\u0648\u0646'],
  'page.analytics': ['Analytics', '\u0627\u0644\u062A\u062D\u0644\u064A\u0644\u0627\u062A'],
  'page.settings': ['Settings', '\u0627\u0644\u0625\u0639\u062F\u0627\u062F\u0627\u062A'],
  'page.today': ['Today Tasks', '\u0645\u0647\u0627\u0645 \u0627\u0644\u064A\u0648\u0645'],
  'page.deal_requests': ['Deal Requests', '\u0637\u0644\u0628\u0627\u062A \u0627\u0644\u0635\u0641\u0642\u0627\u062A'],
  'page.maintenance': ['Maintenance', '\u0627\u0644\u0635\u064A\u0627\u0646\u0629'],
  'page.tasks': ['Tasks', '\u0627\u0644\u0645\u0647\u0627\u0645'],
  'page.assistant': ['AI Assistant', '\u0627\u0644\u0645\u0633\u0627\u0639\u062F \u0627\u0644\u0630\u0643\u064A'],
  'page.brokers': ['Brokers monitor', '\u0627\u0644\u0648\u0633\u0637\u0627\u0621'],
  'page.commissions': ['Broker commissions', '\u0627\u0644\u0639\u0645\u0648\u0644\u0627\u062A'],
  'page.stale_leads': ['Stale leads', '\u0627\u0644\u0639\u0645\u0644\u0627\u0621 \u0627\u0644\u0631\u0627\u0643\u062F\u0648\u0646'],
  'page.campaigns': ['Ad campaigns', '\u0627\u0644\u062D\u0645\u0644\u0627\u062A \u0627\u0644\u0625\u0639\u0644\u0627\u0646\u064A\u0629'],
  'page.project_media': ['Project media', '\u0648\u0633\u0627\u0626\u0637 \u0627\u0644\u0645\u0634\u0627\u0631\u064A\u0639'],
  'page.closure_tasks': ['My closure tasks', '\u0645\u0647\u0627\u0645 \u0627\u0644\u0625\u063A\u0644\u0627\u0642'],
};
const PERM_ACTION_LABELS = {
  'leads.create': ['Create leads', '\u0625\u0646\u0634\u0627\u0621 \u0639\u0645\u064A\u0644'],
  'leads.edit': ['Edit leads', '\u062A\u0639\u062F\u064A\u0644 \u0639\u0645\u064A\u0644'],
  'leads.delete': ['Delete leads', '\u062D\u0630\u0641 \u0639\u0645\u064A\u0644'],
  'leads.reassign': ['Reassign leads', '\u0625\u0639\u0627\u062F\u0629 \u062A\u0639\u064A\u064A\u0646 \u0627\u0644\u0639\u0645\u0644\u0627\u0621'],
  'leads.directive': ['Lead directives', '\u062A\u0648\u062C\u064A\u0647 \u0627\u0644\u0639\u0645\u0644\u0627\u0621'],
  'units.create': ['Create units', '\u0625\u0646\u0634\u0627\u0621 \u0648\u062D\u062F\u0629'],
  'units.edit': ['Edit units', '\u062A\u0639\u062F\u064A\u0644 \u0648\u062D\u062F\u0629'],
  'deals.create': ['Register deals', '\u062A\u0633\u062C\u064A\u0644 \u0635\u0641\u0642\u0629'],
  'deals.edit': ['Edit deals', '\u062A\u0639\u062F\u064A\u0644 \u0635\u0641\u0642\u0629'],
  'deals.delete': ['Delete deals', '\u062D\u0630\u0641 \u0635\u0641\u0642\u0629'],
  'deals.request': ['Request deals', '\u0637\u0644\u0628 \u0635\u0641\u0642\u0629'],
  'deals.approve': ['Approve deals', '\u0627\u0639\u062A\u0645\u0627\u062F \u0627\u0644\u0635\u0641\u0642\u0627\u062A'],
  'brokers.create': ['Add brokers', '\u0625\u0636\u0627\u0641\u0629 \u0648\u0633\u064A\u0637'],
  'brokers.edit': ['Edit / disable brokers', '\u062A\u0639\u062F\u064A\u0644 / \u062A\u0639\u0637\u064A\u0644 \u0648\u0633\u064A\u0637'],
  'brokers.delete': ['Delete brokers', '\u062D\u0630\u0641 \u0648\u0633\u064A\u0637'],
  'tasks.create': ['Create tasks', '\u0625\u0646\u0634\u0627\u0621 \u0645\u0647\u0645\u0629'],
  'tasks.edit': ['Edit / comment tasks', '\u062A\u0639\u062F\u064A\u0644 / \u0627\u0644\u062A\u0639\u0644\u064A\u0642 \u0639\u0644\u0649 \u0627\u0644\u0645\u0647\u0627\u0645'],
  'tasks.delete': ['Delete tasks', '\u062D\u0630\u0641 \u0645\u0647\u0645\u0629'],
  'campaigns.create': ['Create / edit campaigns', '\u0625\u0646\u0634\u0627\u0621 / \u062A\u0639\u062F\u064A\u0644 \u0627\u0644\u062D\u0645\u0644\u0627\u062A'],
  'campaigns.publish': ['Publish campaigns', '\u0646\u0634\u0631 \u0627\u0644\u062D\u0645\u0644\u0627\u062A'],
  'project_media.manage': ['Manage project media', '\u0625\u062F\u0627\u0631\u0629 \u0648\u0633\u0627\u0626\u0637 \u0627\u0644\u0645\u0634\u0627\u0631\u064A\u0639'],
};

// Shared checkbox grid used by both the per-user editor and the roles manager.
function PermissionGrid({ title, keys, labels, state, toggle, lang }) {
  const lbl = (arr) => (lang === 'ar' ? arr[1] : arr[0]);
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontWeight: 800, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
        {keys.map(k => (
          <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem', background: state[k] ? 'rgba(34,197,94,0.06)' : 'transparent' }}>
            <input type="checkbox" checked={!!state[k]} onChange={() => toggle(k)} />
            <span>{labels[k] ? lbl(labels[k]) : k}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function UserPermissions({ lang }) {
  const PAGE_LABELS = PERM_PAGE_LABELS;
  const ACTION_LABELS = PERM_ACTION_LABELS;

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [state, setState] = useState({}); // key -> bool (effective)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const [uRes, rRes] = await Promise.all([
        axios.get('/api/auth/users'),
        axios.get('/api/auth/roles'),
      ]);
      // Only non-admin, non-broker employees are governed by overrides.
      setUsers((uRes.data.users || []).filter(u => u.role !== 'admin' && u.role !== 'broker'));
      setRoles(rRes.data.roles || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadUsers(); }, []);

  const selectedUser = users.find(u => String(u.id) === String(selectedId));

  // Effective checkbox state: per-user override wins, else the assigned named
  // role's map, else the fixed-role default. Mirrors the server merge order.
  const buildState = (u) => {
    const perms = u.permissions || null;
    const rolePerms = (roles.find(r => String(r.id) === String(u.custom_role_id)) || {}).permissions || null;
    const eff = {};
    [...PAGE_KEYS, ...ACTION_KEYS].forEach(k => {
      if (perms && Object.prototype.hasOwnProperty.call(perms, k)) eff[k] = perms[k] === true;
      else if (rolePerms && Object.prototype.hasOwnProperty.call(rolePerms, k)) eff[k] = rolePerms[k] === true;
      else eff[k] = roleDefault(u.role, k);
    });
    return eff;
  };

  const onSelect = (id) => {
    setSelectedId(id);
    setMsg(null);
    const u = users.find(x => String(x.id) === String(id));
    setState(u ? buildState(u) : {});
  };

  const toggle = (k) => setState(s => ({ ...s, [k]: !s[k] }));

  const assignRole = async (roleId) => {
    if (!selectedUser) return;
    setSaving(true);
    setMsg(null);
    try {
      const res = await axios.patch(`/api/auth/users/${selectedUser.id}/role`, { custom_role_id: roleId || null });
      const updated = { ...selectedUser, custom_role_id: res.data.custom_role_id };
      setUsers(us => us.map(u => u.id === selectedUser.id ? updated : u));
      setState(buildState(updated));
      setMsg({ type: 'success', text: lang === 'ar' ? '\u062A\u0645 \u062A\u0639\u064A\u064A\u0646 \u0627\u0644\u062F\u0648\u0631' : 'Role assigned' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || (lang === 'ar' ? '\u0641\u0634\u0644 \u0627\u0644\u062A\u0639\u064A\u064A\u0646' : 'Failed to assign role') });
    }
    setSaving(false);
  };

  const save = async () => {
    if (!selectedUser) return;
    setSaving(true);
    setMsg(null);
    try {
      const res = await axios.patch(`/api/auth/users/${selectedUser.id}/permissions`, { permissions: state });
      setUsers(us => us.map(u => u.id === selectedUser.id ? { ...u, permissions: res.data.permissions } : u));
      setMsg({ type: 'success', text: lang === 'ar' ? '\u062A\u0645 \u062D\u0641\u0638 \u0627\u0644\u0635\u0644\u0627\u062D\u064A\u0627\u062A' : 'Permissions saved' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || (lang === 'ar' ? '\u0641\u0634\u0644 \u0627\u0644\u062D\u0641\u0638' : 'Failed to save') });
    }
    setSaving(false);
  };

  const resetDefaults = async () => {
    if (!selectedUser) return;
    setSaving(true);
    setMsg(null);
    try {
      await axios.patch(`/api/auth/users/${selectedUser.id}/permissions`, { permissions: null });
      const updated = { ...selectedUser, permissions: null };
      setUsers(us => us.map(u => u.id === selectedUser.id ? updated : u));
      setState(buildState(updated));
      setMsg({ type: 'success', text: lang === 'ar' ? '\u062A\u0645\u062A \u0627\u0644\u0625\u0639\u0627\u062F\u0629 \u0625\u0644\u0649 \u0627\u0644\u0627\u0641\u062A\u0631\u0627\u0636\u064A' : 'Reset to role defaults' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || (lang === 'ar' ? '\u0641\u0634\u0644 \u0627\u0644\u0625\u0639\u0627\u062F\u0629' : 'Failed to reset') });
    }
    setSaving(false);
  };

  const roleLabel = (r) => (lang === 'ar' && r.name_ar ? r.name_ar : r.name);

  return (
    <div className="card mb-6">
      <div className="card-header">
        <h3>{lang === 'ar' ? '\u0627\u0644\u0635\u0644\u0627\u062D\u064A\u0627\u062A \u0648\u0627\u0644\u0623\u0630\u0648\u0646\u0627\u062A' : 'Roles & Permissions'}</h3>
        {selectedUser && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={resetDefaults} disabled={saving}>
              {lang === 'ar' ? '\u0625\u0639\u0627\u062F\u0629 \u0644\u0644\u0627\u0641\u062A\u0631\u0627\u0636\u064A' : 'Reset to default'}
            </button>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
              {saving ? '...' : (lang === 'ar' ? '\u062D\u0641\u0638' : 'Save')}
            </button>
          </div>
        )}
      </div>

      <p className="text-secondary" style={{ marginBottom: 16, fontSize: '0.85rem' }}>
        {lang === 'ar'
          ? '\u0627\u062E\u062A\u0631 \u0645\u0648\u0638\u0641\u0627\u064B \u0644\u062A\u062E\u0635\u064A\u0635 \u0627\u0644\u0635\u0641\u062D\u0627\u062A \u0648\u0627\u0644\u0625\u062C\u0631\u0627\u0621\u0627\u062A \u0627\u0644\u0645\u0633\u0645\u0648\u062D\u0629 \u0644\u0647. \u062F\u0648\u0646 \u062A\u062E\u0635\u064A\u0635\u060C \u062A\u064F\u0637\u0628\u0651\u0642 \u0635\u0644\u0627\u062D\u064A\u0627\u062A \u0627\u0644\u062F\u0648\u0631 \u0627\u0644\u0627\u0641\u062A\u0631\u0627\u0636\u064A\u0629.'
          : 'Pick an employee to fine-tune which pages and actions they can use. With no override, the role\u2019s default access applies.'}
      </p>

      {msg && (
        <div style={{
          background: msg.type === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
          border: `1px solid ${msg.type === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`,
          borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: '0.85rem',
          color: msg.type === 'error' ? 'var(--error)' : 'var(--success)',
        }}>{msg.text}</div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <div className="input-group" style={{ flex: '1 1 280px', maxWidth: 360, marginBottom: 0 }}>
          <label>{lang === 'ar' ? '\u0627\u0644\u0645\u0648\u0638\u0641' : 'Employee'}</label>
          {loading ? (
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>...</div>
          ) : (
            <select value={selectedId} onChange={e => onSelect(e.target.value)}>
              <option value="">{lang === 'ar' ? '\u2014 \u0627\u062E\u062A\u0631 \u0645\u0648\u0638\u0641\u0627\u064B \u2014' : '\u2014 Select an employee \u2014'}</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.role}){u.permissions ? (lang === 'ar' ? ' \u2014 \u0645\u062E\u0635\u0635' : ' \u2014 custom') : ''}
                </option>
              ))}
            </select>
          )}
        </div>
        {selectedUser && (
          <div className="input-group" style={{ flex: '1 1 280px', maxWidth: 360, marginBottom: 0 }}>
            <label>{lang === 'ar' ? '\u0627\u0644\u062F\u0648\u0631 \u0627\u0644\u0645\u0633\u0645\u0651\u0649' : 'Named role'}</label>
            <select value={selectedUser.custom_role_id || ''} onChange={e => assignRole(e.target.value)} disabled={saving}>
              <option value="">{lang === 'ar' ? '\u2014 \u0628\u062F\u0648\u0646 (\u0627\u0641\u062A\u0631\u0627\u0636\u064A \u0627\u0644\u062F\u0648\u0631) \u2014' : '\u2014 None (role default) \u2014'}</option>
              {roles.map(r => (
                <option key={r.id} value={r.id}>{roleLabel(r)}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {selectedUser && (
        <>
          <PermissionGrid title={lang === 'ar' ? '\u0627\u0644\u0635\u0641\u062D\u0627\u062A' : 'Pages'} keys={PAGE_KEYS} labels={PAGE_LABELS} state={state} toggle={toggle} lang={lang} />
          <PermissionGrid title={lang === 'ar' ? '\u0627\u0644\u0625\u062C\u0631\u0627\u0621\u0627\u062A' : 'Actions'} keys={ACTION_KEYS} labels={ACTION_LABELS} state={state} toggle={toggle} lang={lang} />
        </>
      )}
    </div>
  );
}

// Manage reusable named roles (e.g. "Client Viewer = dashboard only"). Editing a
// role's permissions propagates live to every user assigned to it.
function RolesManager({ lang }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null); // role id being edited, or 'new'
  const [name, setName] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [state, setState] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/auth/roles');
      setRoles(res.data.roles || []);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const blankState = () => {
    const s = {};
    [...PAGE_KEYS, ...ACTION_KEYS].forEach(k => { s[k] = false; });
    return s;
  };

  const startNew = () => {
    setEditId('new'); setName(''); setNameAr(''); setState(blankState()); setMsg(null);
  };
  const startEdit = (r) => {
    setEditId(r.id); setName(r.name || ''); setNameAr(r.name_ar || '');
    const s = blankState();
    if (r.permissions) Object.keys(r.permissions).forEach(k => { s[k] = r.permissions[k] === true; });
    setState(s); setMsg(null);
  };
  const cancel = () => { setEditId(null); setMsg(null); };
  const toggle = (k) => setState(s => ({ ...s, [k]: !s[k] }));

  const save = async () => {
    if (!name.trim()) {
      setMsg({ type: 'error', text: lang === 'ar' ? '\u0627\u0633\u0645 \u0627\u0644\u062F\u0648\u0631 \u0645\u0637\u0644\u0648\u0628' : 'Role name required' });
      return;
    }
    setSaving(true); setMsg(null);
    try {
      const body = { name: name.trim(), name_ar: nameAr.trim() || null, permissions: state };
      if (editId === 'new') await axios.post('/api/auth/roles', body);
      else await axios.patch(`/api/auth/roles/${editId}`, body);
      await load();
      setEditId(null);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || (lang === 'ar' ? '\u0641\u0634\u0644 \u0627\u0644\u062D\u0641\u0638' : 'Failed to save') });
    }
    setSaving(false);
  };

  const remove = async (r) => {
    if (!window.confirm(lang === 'ar' ? `\u062D\u0630\u0641 \u0627\u0644\u062F\u0648\u0631 "${r.name}"\u061F \u0633\u064A\u062A\u0645 \u0625\u0644\u063A\u0627\u0621 \u062A\u0639\u064A\u064A\u0646\u0647 \u0645\u0646 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645\u064A\u0646.` : `Delete role "${r.name}"? It will be unassigned from any users.`)) return;
    try {
      await axios.delete(`/api/auth/roles/${r.id}`);
      if (editId === r.id) setEditId(null);
      await load();
    } catch {}
  };

  const roleLabel = (r) => (lang === 'ar' && r.name_ar ? r.name_ar : r.name);

  return (
    <div className="card mb-6">
      <div className="card-header">
        <h3>{lang === 'ar' ? '\u0627\u0644\u0623\u062F\u0648\u0627\u0631' : 'Named Roles'}</h3>
        <button className="btn btn-primary btn-sm" onClick={startNew}>
          {lang === 'ar' ? '+ \u062F\u0648\u0631 \u062C\u062F\u064A\u062F' : '+ New role'}
        </button>
      </div>

      <p className="text-secondary" style={{ marginBottom: 16, fontSize: '0.85rem' }}>
        {lang === 'ar'
          ? '\u0623\u0646\u0634\u0626 \u062F\u0648\u0631\u0627\u064B \u0645\u0631\u0629 \u0648\u0627\u062D\u062F\u0629 (\u0645\u062B\u0644 \u00AB\u0639\u0631\u0636 \u0641\u0642\u0637\u00BB) \u062B\u0645 \u0639\u064A\u0651\u0646\u0647 \u0644\u0639\u062F\u0629 \u0645\u0648\u0638\u0641\u064A\u0646. \u062A\u0639\u062F\u064A\u0644 \u0627\u0644\u062F\u0648\u0631 \u064A\u064F\u0637\u0628\u0651\u0642 \u0639\u0644\u0649 \u062C\u0645\u064A\u0639 \u0627\u0644\u0645\u0639\u064A\u0651\u0646\u064A\u0646 \u0628\u0647.'
          : 'Create a role once (e.g. \u201CDashboard only\u201D) and assign it to many employees. Editing a role updates everyone assigned to it.'}
      </p>

      {msg && (
        <div style={{
          background: msg.type === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
          border: `1px solid ${msg.type === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`,
          borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: '0.85rem',
          color: msg.type === 'error' ? 'var(--error)' : 'var(--success)',
        }}>{msg.text}</div>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>...</div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: editId ? 20 : 0 }}>
          {roles.length === 0 && (
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              {lang === 'ar' ? '\u0644\u0627 \u062A\u0648\u062C\u062F \u0623\u062F\u0648\u0627\u0631 \u0628\u0639\u062F.' : 'No roles yet.'}
            </div>
          )}
          {roles.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', border: `1px solid ${String(editId) === String(r.id) ? 'var(--primary)' : 'var(--border)'}`, borderRadius: 8, fontSize: '0.85rem' }}>
              <button onClick={() => startEdit(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', fontWeight: 600 }}>{roleLabel(r)}</button>
              <span onClick={() => remove(r)} style={{ cursor: 'pointer', color: 'var(--error)', fontWeight: 700, paddingLeft: 4 }}>{'\u00D7'}</span>
            </div>
          ))}
        </div>
      )}

      {editId && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
            <div className="input-group" style={{ flex: '1 1 240px', maxWidth: 320, marginBottom: 0 }}>
              <label>{lang === 'ar' ? '\u0627\u0633\u0645 \u0627\u0644\u062F\u0648\u0631 (EN)' : 'Role name (EN)'}</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder={lang === 'ar' ? '\u0645\u062B\u0627\u0644: Client Viewer' : 'e.g. Client Viewer'} />
            </div>
            <div className="input-group" style={{ flex: '1 1 240px', maxWidth: 320, marginBottom: 0 }}>
              <label>{lang === 'ar' ? '\u0627\u0644\u0627\u0633\u0645 \u0628\u0627\u0644\u0639\u0631\u0628\u064A\u0629' : 'Name (AR)'}</label>
              <input value={nameAr} onChange={e => setNameAr(e.target.value)} dir="rtl" />
            </div>
          </div>
          <PermissionGrid title={lang === 'ar' ? '\u0627\u0644\u0635\u0641\u062D\u0627\u062A' : 'Pages'} keys={PAGE_KEYS} labels={PERM_PAGE_LABELS} state={state} toggle={toggle} lang={lang} />
          <PermissionGrid title={lang === 'ar' ? '\u0627\u0644\u0625\u062C\u0631\u0627\u0621\u0627\u062A' : 'Actions'} keys={ACTION_KEYS} labels={PERM_ACTION_LABELS} state={state} toggle={toggle} lang={lang} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={cancel} disabled={saving}>
              {lang === 'ar' ? '\u0625\u0644\u063A\u0627\u0621' : 'Cancel'}
            </button>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
              {saving ? '...' : (lang === 'ar' ? '\u062D\u0641\u0638 \u0627\u0644\u062F\u0648\u0631' : 'Save role')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Audit Log — admin-only view of every create/update/delete across the app ──
function AuditLog({ lang }) {
  const isAr = lang === 'ar';
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fAction, setFAction] = useState('');
  const [fEntity, setFEntity] = useState('');
  const [fUser, setFUser] = useState('');
  const [expanded, setExpanded] = useState(null);
  const PAGE = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE, offset: page * PAGE };
      if (fAction) params.action = fAction;
      if (fEntity) params.entity = fEntity;
      if (fUser) params.user = fUser;
      const res = await axios.get('/api/audit', { params });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch { setEntries([]); setTotal(0); }
    setLoading(false);
  }, [page, fAction, fEntity, fUser]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [fAction, fEntity, fUser]);

  const actionColor = (a) => (a === 'create' ? '#22c55e' : a === 'delete' ? '#ef4444' : '#6366f1');
  const actionLabel = (a) => isAr ? ({ create: 'إنشاء', update: 'تعديل', delete: 'حذف' }[a] || a) : a;
  const fmtTime = (dt) => dt ? new Date(dt).toLocaleString(isAr ? 'ar-EG' : 'en-GB') : '—';

  return (
    <div className="card mb-6">
      <div className="card-header"><h3>{isAr ? 'سجل التدقيق' : 'Audit Log'}</h3></div>
      <p className="text-secondary" style={{ marginBottom: 14, fontSize: '0.85rem' }}>
        {isAr ? 'كل عمليات الإنشاء والتعديل والحذف عبر النظام — من قام بها ومتى.' : 'Every create, update and delete across the system — who did it and when.'}
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <select value={fAction} onChange={e => setFAction(e.target.value)} style={{ width: 'auto', padding: '6px 10px', fontSize: '0.82rem' }}>
          <option value="">{isAr ? 'كل العمليات' : 'All actions'}</option>
          <option value="create">{isAr ? 'إنشاء' : 'create'}</option>
          <option value="update">{isAr ? 'تعديل' : 'update'}</option>
          <option value="delete">{isAr ? 'حذف' : 'delete'}</option>
        </select>
        <input value={fEntity} onChange={e => setFEntity(e.target.value)} placeholder={isAr ? 'العنصر (leads، deals…)' : 'Entity (leads, deals…)'} style={{ width: 'auto', padding: '6px 10px', fontSize: '0.82rem', direction: 'ltr' }} />
        <input value={fUser} onChange={e => setFUser(e.target.value)} placeholder={isAr ? 'المستخدم' : 'User'} style={{ width: 'auto', padding: '6px 10px', fontSize: '0.82rem' }} />
      </div>

      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>...</div>
      ) : entries.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{isAr ? 'لا توجد سجلات' : 'No entries'}</div>
      ) : (
        <div className="data-table-wrapper" style={{ maxHeight: 'calc(100vh - 360px)', overflowY: 'auto' }}>
          <table className="data-table" style={{ fontSize: '0.8rem' }}>
            <thead>
              <tr>
                <th>{isAr ? 'الوقت' : 'Time'}</th>
                <th>{isAr ? 'المستخدم' : 'User'}</th>
                <th>{isAr ? 'العملية' : 'Action'}</th>
                <th>{isAr ? 'العنصر' : 'Entity'}</th>
                <th>{isAr ? 'المسار' : 'Path'}</th>
                <th style={{ textAlign: 'center' }}>{isAr ? 'النتيجة' : 'Result'}</th>
                <th>{isAr ? 'التفاصيل' : 'Details'}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id}>
                  <td style={{ whiteSpace: 'nowrap', fontSize: '0.74rem', color: 'var(--text-secondary)', direction: 'ltr' }}>{fmtTime(e.created_at)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{e.user_name || '—'}{e.user_role ? <span style={{ color: 'var(--text-secondary)', fontSize: '0.7rem' }}> ({e.user_role})</span> : null}</td>
                  <td><span style={{ color: actionColor(e.action), fontWeight: 800, fontSize: '0.74rem' }}>{actionLabel(e.action)}</span></td>
                  <td style={{ direction: 'ltr', fontSize: '0.76rem' }}>{e.entity || '—'}{e.entity_id ? ' #' + e.entity_id : ''}</td>
                  <td style={{ direction: 'ltr', fontSize: '0.72rem', color: 'var(--text-secondary)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.method} {e.path}</td>
                  <td style={{ textAlign: 'center' }}><span style={{ color: e.status_code >= 400 ? '#ef4444' : '#22c55e', fontWeight: 700 }}>{e.status_code}</span></td>
                  <td>
                    {e.payload ? (
                      <button onClick={() => setExpanded(expanded === e.id ? null : e.id)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '2px 8px', fontSize: '0.7rem', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                        {expanded === e.id ? (isAr ? 'إخفاء' : 'hide') : (isAr ? 'عرض' : 'view')}
                      </button>
                    ) : '—'}
                    {expanded === e.id && (
                      <pre style={{ direction: 'ltr', textAlign: 'left', whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '0.7rem', background: 'var(--bg-primary)', padding: 8, borderRadius: 6, marginTop: 6, maxWidth: 360 }}>{e.payload}</pre>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 12 }}>
          <button className="btn btn-secondary btn-sm" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>{isAr ? 'السابق' : 'Prev'}</button>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{page * PAGE + 1}–{Math.min((page + 1) * PAGE, total)} / {total}</span>
          <button className="btn btn-secondary btn-sm" disabled={(page + 1) * PAGE >= total} onClick={() => setPage(p => p + 1)}>{isAr ? 'التالي' : 'Next'}</button>
        </div>
      )}
    </div>
  );
}

function Settings() {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // Non-admins with a brokers.* permission get the Brokers tab (add/list broker
  // accounts). Every other admin tab stays admin-only.
  const canManageBrokers = can(user, 'brokers.create') || can(user, 'brokers.edit') || can(user, 'brokers.delete');
  const [toasts, setToasts] = useState([]);
  const [syncStatuses, setSyncStatuses] = useState({});
  // Default to employees for admins; the Brokers tab for broker managers; else social.
  const [activeTab, setActiveTab] = useState(isAdmin ? 'employees' : (canManageBrokers ? 'brokers' : 'social'));
  const [socialLoaded, setSocialLoaded] = useState(false);

  const [snap, setSnap] = useState({ client_id: '', client_secret: '', refresh_token: '', ad_account_id: '' });
  const [tiktok, setTiktok] = useState({ app_id: '', secret: '', access_token: '', advertiser_id: '' });
  const [clarity, setClarity] = useState({ clarity_project_id: '', clarity_api_key: '', mode: 'api', clarity_email: '', clarity_password: '' });
  const [testing, setTesting] = useState({ snapchat: false, tiktok: false, clarity: false });
  const [syncing, setSyncing] = useState({ snapchat: false, tiktok: false, clarity: false });

  // Lazy load credentials only when the Social tab is first opened.
  useEffect(() => {
    if (activeTab !== 'social' || socialLoaded) return;
    setSocialLoaded(true);
    async function load() {
      const [snapRes, tiktokRes, clarityRes, syncRes] = await Promise.all([
        getCredentials('snapchat'), getCredentials('tiktok'), getCredentials('clarity'), getSyncStatus(),
      ]);
      if (snapRes.credentials) setSnap(prev => { const u = { ...prev }; Object.keys(snapRes.credentials).forEach(k => { u[k] = snapRes.credentials[k]; }); return u; });
      if (tiktokRes.credentials) setTiktok(prev => { const u = { ...prev }; Object.keys(tiktokRes.credentials).forEach(k => { u[k] = tiktokRes.credentials[k]; }); return u; });
      if (clarityRes.credentials) setClarity(prev => { const u = { ...prev }; Object.keys(clarityRes.credentials).forEach(k => { u[k] = clarityRes.credentials[k]; }); return u; });
      if (syncRes.status) { const m = {}; syncRes.status.forEach(s => { m[s.platform] = s; }); setSyncStatuses(m); }
    }
    load();
  }, [activeTab, socialLoaded]);

  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => { setToasts(prev => prev.filter(t => t.id !== id)); }, 4000);
  };

  const handleSave = async (platform, creds) => {
    const result = await saveCredentials(platform, creds);
    if (result.error) showToast(`Error: ${result.error}`, 'error');
    else showToast(`${platform} credentials saved`);
  };

  const handleTest = async (platform, creds) => {
    setTesting(p => ({ ...p, [platform]: true }));
    const result = await testConnection(platform, creds);
    setTesting(p => ({ ...p, [platform]: false }));
    if (result.success) { showToast(result.message || `${platform} connected!`); const syncRes = await getSyncStatus(); if (syncRes.status) { const m = {}; syncRes.status.forEach(s => { m[s.platform] = s; }); setSyncStatuses(m); } }
    else showToast(result.error || 'Connection failed', 'error');
  };

  const handleSync = async (platform) => {
    setSyncing(p => ({ ...p, [platform]: true }));
    const result = await triggerSync(platform);
    setSyncing(p => ({ ...p, [platform]: false }));
    if (result.error) showToast(`Sync failed: ${result.error}`, 'error');
    else if (result.skipped) showToast(`${platform} sync skipped: ${result.reason}`, 'error');
    else showToast(`${platform} synced ${result.recordCount} records`);
    const syncRes = await getSyncStatus(); if (syncRes.status) { const m = {}; syncRes.status.forEach(s => { m[s.platform] = s; }); setSyncStatuses(m); }
  };

  const updateSnap = (k, v) => setSnap(p => ({ ...p, [k]: v }));
  const updateTiktok = (k, v) => setTiktok(p => ({ ...p, [k]: v }));
  const updateClarity = (k, v) => setClarity(p => ({ ...p, [k]: v }));

  const tabs = [
    ...(isAdmin ? [
      { key: 'employees', label: t('settingsTabEmployees') },
      { key: 'brokers', label: t('settingsTabBrokers') },
      { key: 'distribution', label: lang === 'ar' ? '\u062A\u0648\u0632\u064A\u0639 \u0627\u0644\u0639\u0645\u0644\u0627\u0621' : 'Lead Distribution' },
      { key: 'checklist', label: lang === 'ar' ? '\u0645\u0647\u0627\u0645 \u0625\u063A\u0644\u0627\u0642 \u0627\u0644\u0635\u0641\u0642\u0629' : 'Deal-closure tasks' },
      { key: 'permissions', label: lang === 'ar' ? '\u0627\u0644\u0635\u0644\u0627\u062D\u064A\u0627\u062A' : 'Permissions' },
      { key: 'audit', label: lang === 'ar' ? '\u0633\u062C\u0644 \u0627\u0644\u062A\u062F\u0642\u064A\u0642' : 'Audit Log' },
      { key: 'leadforms', label: lang === 'ar' ? '\u0631\u0628\u0637 \u0623\u0633\u0626\u0644\u0629 \u0627\u0644\u0646\u0645\u0627\u0630\u062C' : 'Lead-form questions' },
    ] : (canManageBrokers ? [
      { key: 'brokers', label: t('settingsTabBrokers') },
    ] : [])),
    { key: 'social', label: t('settingsTabSocial') },
  ];

  return (
    <div>
      <div className="toast-container">
        {toasts.map(t => <div key={t.id} className={`toast toast-${t.type}`}>{t.message}</div>)}
      </div>

      {/* Tab bar */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          marginBottom: 20,
          borderBottom: '1px solid var(--border)',
          flexWrap: 'wrap',
        }}
      >
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 18px',
              fontSize: '0.88rem',
              fontWeight: 700,
              background: 'transparent',
              border: 'none',
              color: activeTab === tab.key ? 'var(--accent)' : 'var(--text-secondary)',
              borderBottom: activeTab === tab.key ? '2px solid var(--accent)' : '2px solid transparent',
              marginBottom: -1,
              cursor: 'pointer',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'employees' && isAdmin && <UserManagement t={t} lang={lang} kind="employee" />}
      {activeTab === 'brokers' && (isAdmin || canManageBrokers) && <UserManagement t={t} lang={lang} kind="broker" />}
      {activeTab === 'distribution' && isAdmin && <LeadDistribution lang={lang} />}
      {activeTab === 'checklist' && isAdmin && <ChecklistAssignments lang={lang} />}
      {activeTab === 'permissions' && isAdmin && (
        <>
          <RolesManager lang={lang} />
          <UserPermissions lang={lang} />
        </>
      )}
      {activeTab === 'audit' && isAdmin && <AuditLog lang={lang} />}
      {activeTab === 'leadforms' && isAdmin && <QuestionMapEditor lang={lang} />}

      {activeTab === 'social' && (<>
      <h2 style={{ marginBottom: 24, fontSize: '1.25rem' }}>{t('platformCredentials')}</h2>
      <p className="text-secondary" style={{ marginBottom: 24, fontSize: '0.85rem' }}>{t('credentialsDesc')}</p>

      {/* Snapchat */}
      <div className="card mb-6">
        <div className="card-header">
          <h3><span className="badge badge-snapchat" style={{ marginRight: 10 }}>SC</span>{t('snapchatAds')}</h3>
          <SyncStatusBadge status={syncStatuses.snapchat} t={t} />
        </div>
        <div className="grid grid-2" style={{ gap: '0 20px' }}>
          <TextInput label={t('clientId')} value={snap.client_id} onChange={v => updateSnap('client_id', v)} placeholder={t('enterClientId')} />
          <PasswordInput label={t('clientSecret')} value={snap.client_secret} onChange={v => updateSnap('client_secret', v)} placeholder={t('enterClientSecret')} showLabel={t('show')} hideLabel={t('hide')} />
          <PasswordInput label={t('refreshToken')} value={snap.refresh_token} onChange={v => updateSnap('refresh_token', v)} placeholder={t('enterRefreshToken')} showLabel={t('show')} hideLabel={t('hide')} />
          <TextInput label={t('adAccountId')} value={snap.ad_account_id} onChange={v => updateSnap('ad_account_id', v)} placeholder={t('enterAdAccountId')} />
        </div>
        <div className="btn-group mt-4">
          <button className="btn btn-primary" onClick={() => handleSave('snapchat', snap)}>{t('save')}</button>
          <button className="btn btn-secondary" onClick={() => handleTest('snapchat', snap)} disabled={testing.snapchat}>{testing.snapchat ? t('testing') : t('testAndSync')}</button>
          <button className="btn btn-secondary" onClick={() => handleSync('snapchat')} disabled={syncing.snapchat}>{syncing.snapchat ? t('syncing') : t('syncNow')}</button>
        </div>
      </div>

      {/* TikTok */}
      <div className="card mb-6">
        <div className="card-header">
          <h3><span className="badge badge-tiktok" style={{ marginRight: 10 }}>TT</span>{t('tiktokAds')}</h3>
          <SyncStatusBadge status={syncStatuses.tiktok} t={t} />
        </div>
        <div className="grid grid-2" style={{ gap: '0 20px' }}>
          <TextInput label={t('appId')} value={tiktok.app_id} onChange={v => updateTiktok('app_id', v)} placeholder={t('enterAppId')} />
          <PasswordInput label={t('secret')} value={tiktok.secret} onChange={v => updateTiktok('secret', v)} placeholder={t('enterSecret')} showLabel={t('show')} hideLabel={t('hide')} />
          <PasswordInput label={t('accessToken')} value={tiktok.access_token} onChange={v => updateTiktok('access_token', v)} placeholder={t('enterAccessToken')} showLabel={t('show')} hideLabel={t('hide')} />
          <TextInput label={t('advertiserId')} value={tiktok.advertiser_id} onChange={v => updateTiktok('advertiser_id', v)} placeholder={t('enterAdvertiserId')} />
        </div>
        <div className="btn-group mt-4">
          <button className="btn btn-primary" onClick={() => handleSave('tiktok', tiktok)}>{t('save')}</button>
          <button className="btn btn-secondary" onClick={() => handleTest('tiktok', tiktok)} disabled={testing.tiktok}>{testing.tiktok ? t('testing') : t('testAndSync')}</button>
          <button className="btn btn-secondary" onClick={() => handleSync('tiktok')} disabled={syncing.tiktok}>{syncing.tiktok ? t('syncing') : t('syncNow')}</button>
        </div>
      </div>

      {/* Clarity */}
      <div className="card mb-6">
        <div className="card-header">
          <h3><span className="badge badge-clarity" style={{ marginRight: 10 }}>CL</span>{t('microsoftClarity')}</h3>
          <SyncStatusBadge status={syncStatuses.clarity} t={t} />
        </div>
        <div className="warning-banner">&#9888; {t('clarityWarning')}</div>
        <div className="input-group">
          <label>{t('mode')}</label>
          <div className="tabs" style={{ display: 'inline-flex', marginBottom: 16 }}>
            <button className={`tab ${clarity.mode === 'api' ? 'active' : ''}`} onClick={() => updateClarity('mode', 'api')}>{t('apiKeyMode')}</button>
            <button className={`tab ${clarity.mode === 'login' ? 'active' : ''}`} onClick={() => updateClarity('mode', 'login')}>{t('loginFallback')}</button>
          </div>
        </div>
        <div className="grid grid-2" style={{ gap: '0 20px' }}>
          <TextInput label={t('projectId')} value={clarity.clarity_project_id} onChange={v => updateClarity('clarity_project_id', v)} placeholder={t('enterProjectId')} />
          {clarity.mode === 'api' && <PasswordInput label={t('apiKey')} value={clarity.clarity_api_key} onChange={v => updateClarity('clarity_api_key', v)} placeholder={t('enterApiKey')} showLabel={t('show')} hideLabel={t('hide')} />}
          {clarity.mode === 'login' && (<>
            <TextInput label={t('email')} value={clarity.clarity_email} onChange={v => updateClarity('clarity_email', v)} placeholder={t('enterEmail')} />
            <PasswordInput label={t('password')} value={clarity.clarity_password} onChange={v => updateClarity('clarity_password', v)} placeholder={t('enterPassword')} showLabel={t('show')} hideLabel={t('hide')} />
          </>)}
        </div>
        <div className="btn-group mt-4">
          <button className="btn btn-primary" onClick={() => handleSave('clarity', clarity)}>{t('save')}</button>
          <button className="btn btn-secondary" onClick={() => handleTest('clarity', clarity)} disabled={testing.clarity}>{testing.clarity ? t('testing') : t('testAndSync')}</button>
          <button className="btn btn-secondary" onClick={() => handleSync('clarity')} disabled={syncing.clarity}>{syncing.clarity ? t('syncing') : t('syncNow')}</button>
        </div>
      </div>
      </>)}
    </div>
  );
}

export default Settings;
